import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@monksflow/monks-ui';
import { KNOWLEDGE_BASES_QUERY_KEYS } from '@/entities/knowledge-base';
import { useCurrentOrganizationWorkspaces } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import {
  KnowledgeBaseFormData,
  KnowledgeBaseFormVisibility,
  useKnowledgeBaseActions,
  useKnowledgeBaseDetail,
  useKnowledgeBaseForm,
} from '@/features/knowledge-bases';
import { useKbWorkspaceSelection } from '@/features/knowledge-bases/model/useKbWorkspaceSelection/useKbWorkspaceSelection';
import { useKnowledgeBaseModal } from '@/features/knowledge-bases/model/knowledge-base-modal.store';
import { withSpaceVisibility } from '@/features/knowledge-bases/model/withSpaceVisibility';
import {
  KnowledgeBaseFormDescriptionV2,
  KnowledgeBaseFormSpaceVisibilityV2,
  KnowledgeBaseFormTitleV2,
} from '../KnowledgeBaseModal/KnowledgeBaseFormV2';
import { FormSidePanel } from '@/shared/ui/layout/FormSidePanel';

/**
 * Right-side counterpart to the centre `KnowledgeBaseModal`, used only by the
 * redesigned workspace (`KnowledgeFilesWorkspace` mounts this alongside
 * `<KnowledgeBaseModal renderForm={false} />`) for both the create and edit
 * form modes. Delete stays on the centre modal's own confirmation, reached
 * from this sheet's Delete action while in edit mode. Reads the same modal
 * store, form hook and action hook the modal uses so the two surfaces never
 * drift on validation or submit behaviour.
 *
 * The fields are the `*V2` twins: the originals are MUI plus monks-ui v3, whose
 * colours are pinned to light whatever the theme, which left this otherwise
 * dark-correct shell holding white tiles and unreadable helper text.
 */
export function CollectionFormSheet() {
  const { t } = useTranslation('knowledge');

  const {
    isKnowledgeBaseModalOpen,
    knowledgeBaseModalType,
    knowledgeBaseModalTypeCreate,
    knowledgeBaseModalTypeEdit,
    closeKnowledgeBaseModal,
  } = useKnowledgeBaseModal();

  const { knowledgeBase } = useKnowledgeBaseDetail();
  const { scope } = useKbWorkspaceSelection(knowledgeBase);
  const { handleSubmit, handleDelete, isSubmitting } = useKnowledgeBaseActions({
    canSelectWorkspaces: true,
  });
  const { formData, setFormData, isFormValid } = useKnowledgeBaseForm(
    knowledgeBaseModalType,
    knowledgeBase,
    { canSelectWorkspaces: true, scope },
  );

  const workspaceOptions = useCurrentOrganizationWorkspaces();
  // Creating always includes the caller's own workspace, so the form keeps it selected. Editing
  // names the workspaces outright: the API adds nothing there, and forcing one in would grant a
  // workspace the editor never chose.
  const ownWorkspaceId = useCurrentWorkspaceId() ?? undefined;
  const lockedWorkspaceId = knowledgeBaseModalTypeEdit ? undefined : ownWorkspaceId;

  const queryClient = useQueryClient();

  const handleSubmitWithInvalidation = async (data: KnowledgeBaseFormData) => {
    await handleSubmit(data, (knowledgeBaseId: string) => {
      void queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.all });
      void queryClient.invalidateQueries({
        queryKey: KNOWLEDGE_BASES_QUERY_KEYS.detail(knowledgeBaseId),
      });
    });
  };

  const isOpen =
    isKnowledgeBaseModalOpen && (knowledgeBaseModalTypeCreate || knowledgeBaseModalTypeEdit);

  const title = knowledgeBaseModalTypeEdit
    ? t('knowledgeBases.collectionFormSheet.editTitle', {
        name: knowledgeBase?.name ?? t('knowledgeBases.knowledgeBaseDocumentsTable.untitled'),
      })
    : t('knowledgeBases.knowledgeBaseForm.newKnowledgeBase');

  // The edit title already names the collection, so a subtitle under it only
  // restates the fields below. Create has a generic title and keeps its guidance.
  const description = knowledgeBaseModalTypeEdit
    ? undefined
    : t('knowledgeBases.collectionFormSheet.createDescription');

  const formAriaLabel = knowledgeBaseModalTypeEdit
    ? t('knowledgeBases.knowledgeBaseForm.editKnowledgeBaseForm')
    : t('knowledgeBases.knowledgeBaseForm.createKnowledgeBaseForm');

  const primaryLabel = knowledgeBaseModalTypeEdit
    ? t('knowledgeBases.knowledgeBaseForm.saveChanges')
    : t('knowledgeBases.knowledgeBaseForm.createKnowledgeBase');

  return (
    <FormSidePanel
      open={isOpen}
      onOpenChange={(open) => !open && closeKnowledgeBaseModal()}
      title={title}
      description={description}
      className="gap-sm"
      footer={
        <>
          {knowledgeBaseModalTypeEdit && (
            <Button variant="ghost" size="lg" onClick={handleDelete}>
              {t('knowledgeBases.knowledgeBaseForm.delete')}
            </Button>
          )}
          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              size="lg"
              onClick={closeKnowledgeBaseModal}
              disabled={isSubmitting}
            >
              {t('knowledgeBases.knowledgeBaseForm.cancel')}
            </Button>
            <Button
              variant="default"
              size="lg"
              onClick={() => void handleSubmitWithInvalidation(formData)}
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? t('knowledgeBases.knowledgeBaseForm.saving') : primaryLabel}
            </Button>
          </div>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmitWithInvalidation(formData);
        }}
        noValidate
        className="flex flex-col gap-md"
        aria-label={formAriaLabel}
      >
        <KnowledgeBaseFormTitleV2
          title={formData.title}
          setTitle={(title) => setFormData({ ...formData, title })}
        />
        <KnowledgeBaseFormDescriptionV2
          description={formData.description}
          setDescription={(description) => setFormData({ ...formData, description })}
        />
        <KnowledgeBaseFormSpaceVisibilityV2
          spaceVisibility={formData.spaceVisibility}
          setSpaceVisibility={(spaceVisibility: KnowledgeBaseFormVisibility) =>
            setFormData(withSpaceVisibility(formData, spaceVisibility, lockedWorkspaceId))
          }
          workspaces={{
            options: workspaceOptions,
            value: formData.workspaceIds,
            onChange: (workspaceIds) => setFormData({ ...formData, workspaceIds }),
            lockedWorkspaceId,
            includeCurrentWorkspace: !knowledgeBaseModalTypeEdit,
          }}
        />
      </form>
    </FormSidePanel>
  );
}

CollectionFormSheet.displayName = 'CollectionFormSheet';
